import { createRateLimitedRequest, type RequestOptions, watchRequest } from '@services/api/utils'
import { type WatchRequestConfig } from '@services/api/declarations'

import { useApiUserStore } from '@services/User/store'

import {
  type PostUserPasswordResetRequest,
  type PostUserPasswordResetValidateRequest,
  type PostUserPasswordResetConfirmRequest,
  type PatchUserChangePasswordRequest,
  type UserStoreShape,
} from '@services/User/declarations'

import {
  postUserPasswordResetConfirmConfig,
  postUserPasswordResetValidateConfig,
  postUserPasswordResetRequestConfig,
  patchUserChangePasswordConfig,
  getUserProfileConfig,
} from '@services/User/api'

// ***
// COMMON
// ***

const serviceName = 'User'

// ***
// REQUESTS
// ***

export function useApiUserStoreRequest() {
  const apiUserStore = useApiUserStore()
  const { sendRequest } = apiUserStore

  // GET USER PROFILE
  function fetchGetUserProfile() {
    createRateLimitedRequest<undefined>({
      sendRequest,
      endpoint: getUserProfileConfig,
      requestConfig: undefined,
      serviceName,
      saveToStore: true,
    })
  }

  function watchGetUserProfile(
    { onRequest, onSuccess, onError }: WatchRequestConfig<UserStoreShape['getUserProfile']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['getUserProfile']>(getUserProfileConfig, {
      onRequest,
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // PATCH USER CHANGE PASSWORD
  function fetchPatchUserChangePassword(
    requestConfig: PatchUserChangePasswordRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PatchUserChangePasswordRequest>({
      sendRequest,
      endpoint: patchUserChangePasswordConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPatchUserChangePassword(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['patchUserChangePassword']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['patchUserChangePassword']>(patchUserChangePasswordConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST USER PASSWORD RESET CONFIRM
  function fetchPostUserPasswordResetConfirm(
    requestConfig: PostUserPasswordResetConfirmRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostUserPasswordResetConfirmRequest>({
      sendRequest,
      endpoint: postUserPasswordResetConfirmConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPostUserPasswordResetConfirm(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['postUserPasswordResetConfirm']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['postUserPasswordResetConfirm']>(postUserPasswordResetConfirmConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST USER PASSWORD RESET REQUEST
  function fetchPostUserPasswordResetRequest(
    requestConfig: PostUserPasswordResetRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostUserPasswordResetRequest>({
      sendRequest,
      endpoint: postUserPasswordResetRequestConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPostUserPasswordResetRequest(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['postUserPasswordResetRequest']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['postUserPasswordResetRequest']>(postUserPasswordResetRequestConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST USER PASSWORD RESET VALIDATE
  function fetchPostUserPasswordResetValidate(
    requestConfig: PostUserPasswordResetValidateRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostUserPasswordResetValidateRequest>({
      sendRequest,
      endpoint: postUserPasswordResetValidateConfig,
      requestConfig,
      serviceName,
      ...options,
    })
  }

  function watchPostUserPasswordResetValidate(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['postUserPasswordResetValidate']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['postUserPasswordResetValidate']>(postUserPasswordResetValidateConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  return {
    store: apiUserStore,
    fetchGetUserProfile,
    fetchPatchUserChangePassword,
    fetchPostUserPasswordResetConfirm,
    fetchPostUserPasswordResetRequest,
    fetchPostUserPasswordResetValidate,
    // watchers
    watchGetUserProfile,
    watchPatchUserChangePassword,
    watchPostUserPasswordResetConfirm,
    watchPostUserPasswordResetRequest,
    watchPostUserPasswordResetValidate,
  }
}
